import { useState } from "react";
import { AlertCircle, CheckCircle2, Clock, ArrowRight, Bot } from "lucide-react";
import type { GeralCalendarEvent } from "@/services/modules/geral";

const moduleLabels: Record<string, string> = {
  marcas: "Marcas",
  patentes: "Patentes",
  di: "DI",
  internacional: "Internacional",
};

export function UrgentTasksList({ eventsData, onAskAI }: { eventsData: GeralCalendarEvent[]; onAskAI?: (query: string) => void }) {
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());

  const handleComplete = (taskId: string) => {
    setCompletedTasks((prev) => new Set(prev).add(taskId));
  };

  if (!eventsData || eventsData.length === 0) {
    return (
      <div className="h-full min-h-[260px] border bg-white p-6" style={{ borderRadius: 16, borderColor: 'rgba(226,235,240,0.5)', boxShadow: '0 1px 3px rgba(13,46,69,0.04), 0 4px 12px rgba(13,46,69,0.02)' }}>
        <div className="flex items-center gap-2 mb-6">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <h2 className="text-sm font-bold text-slate-800">Inbox Zero</h2>
        </div>
        <div className="flex h-[calc(100%-2.5rem)] flex-col items-center justify-center text-center">
          <p className="text-sm text-slate-400 font-medium">Nenhum prazo fatal pendente.</p>
          <p className="text-xs text-slate-400/60 mt-1">Você está no controle da operação.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-[260px] border bg-white p-6" style={{ borderRadius: 16, borderColor: 'rgba(226,235,240,0.5)', boxShadow: '0 1px 3px rgba(13,46,69,0.04), 0 4px 12px rgba(13,46,69,0.02)' }}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <h2 className="text-sm font-bold text-slate-800">Prazos Críticos & Ações</h2>
        </div>
        <span className="text-xs font-bold bg-red-100 text-red-700 px-2.5 py-1 rounded-full">
          {eventsData.length} pendentes
        </span>
      </div>

      <div className="space-y-3">
        {eventsData.map((event, index) => {
          const taskId = `${event.description}-${index}`;
          const isCompleted = completedTasks.has(taskId);
          
          return (
            <div
              key={taskId}
              className={`group relative flex items-start justify-between gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50/50 transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)] overflow-hidden ${
                isCompleted ? "opacity-40 scale-95 pointer-events-none translate-x-4" : "hover:bg-white hover:shadow-sm hover:border-slate-200"
              }`}
            >
              {/* Strike-through line animation */}
              <div 
                className={`absolute left-4 right-4 top-1/2 h-[1px] bg-foreground/30 transition-all duration-500 ${
                  isCompleted ? "w-[calc(100%-2rem)] opacity-100" : "w-0 opacity-0"
                }`}
              />

              <div className="flex-1 min-w-0 z-10">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-200/50 px-1.5 py-0.5 rounded">
                    {moduleLabels[event.module] || event.module}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${event.type === 'Exigência' || event.type === 'Vencimento' ? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50'} px-1.5 py-0.5 rounded`}>
                    {event.type}
                  </span>
                  <div className="flex items-center gap-1 text-[10px] font-medium text-slate-400 ml-auto">
                    <Clock className="w-3 h-3" />
                    Dia {event.date}
                  </div>
                </div>
                
                <h3 className="text-sm font-semibold text-slate-800 truncate pr-4">
                  {event.description}
                </h3>
                
                {/* Loss Aversion Phrasing */}
                {!isCompleted && (
                  <p className="text-xs text-red-600/90 font-medium mt-1.5 flex items-center gap-1">
                    <ArrowRight className="w-3 h-3" />
                    {event.type === 'Exigência' 
                      ? 'Risco de arquivamento caso não seja protocolado.' 
                      : 'Perda de prazo iminente. Aja agora.'}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-1.5 z-10 shrink-0">
                {onAskAI && !isCompleted && (
                  <button
                    onClick={() => onAskAI(`Analise o prazo: "${event.description}" do módulo ${event.module}. O que precisa ser feito, qual o risco se não for atendido e qual o próximo passo concreto?`)}
                    className="flex items-center justify-center h-8 px-2.5 rounded-lg text-xs font-bold transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200"
                    title="Consultar IA sobre este prazo"
                  >
                    <Bot className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  onClick={() => handleComplete(taskId)}
                  className={`flex items-center justify-center h-8 px-3 rounded-lg text-xs font-bold transition-colors ${
                    isCompleted ? "bg-emerald-100 text-emerald-700" : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  {isCompleted ? "Resolvido" : "Resolver"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
